import * as XLSX from 'xlsx';
import { Task, Submission, Participant, ParticipantGroup } from '../types';
import type { AnalysisGrid, GridCell, GridOverview } from '../types/analysisGrid.types';

export const downloadCSV = (content: string, filename: string) => {
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', filename);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }
};

export const escapeCSV = (str: string | undefined | null) => {
    if (!str) return '""';
    // Escape quotes and wrap in quotes
    return `"${String(str).replace(/"/g, '""')}"`;
};

export const exportTasksToCSV = (tasks: Task[], participantsMap: Map<string, Participant>, participantGroups: ParticipantGroup[]) => {
    const headers = ['Task ID', 'Prompt/Title', 'Type', 'Due Date', 'Status', 'Participants Assigned'];

    const rows = tasks.map(task => {
        // Collect participant names based on logic: selectedParticipants, group, or all
        let assignedNames: string[] = [];
        if (task.assignedParticipantIds && task.assignedParticipantIds.length > 0) {
            assignedNames = task.assignedParticipantIds.map(id => participantsMap.get(id)?.name || id);
        } else if (task.customGroup) {
            const group = participantGroups.find(g => g.id === task.customGroup);
            if (group) {
                assignedNames = group.participantIds.map(id => participantsMap.get(id)?.name || id);
            }
        } else {
            assignedNames = Array.from(participantsMap.values()).map(p => p.name);
        }

        return [
            escapeCSV(task.id),
            escapeCSV(task.prompt),
            escapeCSV(task.mediaTypes.join(', ')),
            escapeCSV(task.dueDate),
            escapeCSV(task.status),
            escapeCSV(assignedNames.join(', '))
        ];
    });

    const csvContent = [headers.join(','), ...rows.map(row => row.join(','))].join('\n');
    downloadCSV(csvContent, `tasks_export_${new Date().toISOString().split('T')[0]}.csv`);
};

export const exportSubmissionsToCSV = (submissions: Submission[], tasks: Task[], participantsMap: Map<string, Participant>) => {
    const headers = ['Submission ID', 'Participant Name', 'Task Prompt', 'Media Type', 'Submitted At', 'Is Reviewed', 'Is Starred', 'Tags', 'Transcript', 'Notes'];

    const rows = submissions.map(sub => {
        const participantName = participantsMap.get(sub.participantId)?.name || sub.participantId;
        const taskPrompt = tasks.find(t => t.id === sub.taskId)?.prompt || sub.taskId;

        return [
            escapeCSV(sub.id),
            escapeCSV(participantName),
            escapeCSV(taskPrompt),
            escapeCSV(sub.mediaType),
            escapeCSV(sub.createdAt),
            escapeCSV(sub.isReviewed ? 'Yes' : 'No'),
            escapeCSV(sub.isStarred ? 'Yes' : 'No'),
            escapeCSV(sub.tags.join(', ')),
            escapeCSV(sub.transcript),
            escapeCSV(sub.notes)
        ];
    });

    const csvContent = [headers.join(','), ...rows.map(row => row.join(','))].join('\n');
    downloadCSV(csvContent, `submissions_export_${new Date().toISOString().split('T')[0]}.csv`);
};

/** Converts a 2D array to a CSV string. */
export const buildGridCSVString = (rows: string[][]): string => {
  return rows.map(row => row.map(cell => escapeCSV(cell)).join(',')).join('\n');
};

/** Converts a 2D array to a TSV string (for clipboard paste into Sheets/Excel). */
export const buildGridTSVString = (rows: string[][]): string => {
  return rows.map(row => row.join('\t')).join('\n');
};

/** Downloads the grid as a CSV file. */
export const exportGridToCSV = (
  grid: AnalysisGrid,
  cells: Map<string, GridCell>,
  overviews: Map<string, GridOverview>,
  participants: Participant[],
): void => {
  const rows = buildGridExportRows(grid, cells, overviews, participants);
  const csv = buildGridCSVString(rows);
  const filename = `${grid.name.replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().split('T')[0]}.csv`;
  downloadCSV(csv, filename);
};

/** Copies the grid to clipboard as TSV (pastes into Sheets/Excel with columns). */
export const copyGridToClipboard = async (
  grid: AnalysisGrid,
  cells: Map<string, GridCell>,
  overviews: Map<string, GridOverview>,
  participants: Participant[],
): Promise<void> => {
  const rows = buildGridExportRows(grid, cells, overviews, participants);
  const tsv = buildGridTSVString(rows);
  await navigator.clipboard.writeText(tsv);
};

/** Downloads the grid as a formatted XLSX file. */
export const exportGridToXLSX = (
  grid: AnalysisGrid,
  cells: Map<string, GridCell>,
  overviews: Map<string, GridOverview>,
  participants: Participant[],
): void => {
  const rows = buildGridExportRows(grid, cells, overviews, participants);
  const ws = XLSX.utils.aoa_to_sheet(rows);

  // Bold header row and overview row
  const range = XLSX.utils.decode_range(ws['!ref'] || 'A1');
  for (let c = range.s.c; c <= range.e.c; c++) {
    // Header row (row 0)
    const headerAddr = XLSX.utils.encode_cell({ r: 0, c });
    if (ws[headerAddr]) {
      ws[headerAddr].s = { font: { bold: true } };
    }
    // Overview row (row 1)
    const overviewAddr = XLSX.utils.encode_cell({ r: 1, c });
    if (ws[overviewAddr]) {
      ws[overviewAddr].s = { font: { bold: true } };
    }
  }

  // Auto-width columns
  ws['!cols'] = rows[0].map((_, ci) => {
    const maxLen = rows.reduce((max, row) => Math.max(max, (row[ci] || '').length), 0);
    return { wch: Math.min(Math.max(maxLen, 10), 60) };
  });

  const sheetName = grid.name.slice(0, 31); // Excel 31-char limit
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName);

  const filename = `${grid.name.replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().split('T')[0]}.xlsx`;
  XLSX.writeFile(wb, filename);
};

/**
 * Builds a 2D string array from grid data for export.
 * Row 0: header (questions). Row 1: overview themes. Rows 2+: participant summaries.
 */
export const buildGridExportRows = (
  grid: AnalysisGrid,
  cells: Map<string, GridCell>,
  overviews: Map<string, GridOverview>,
  participants: Participant[],
): string[][] => {
  const columns = grid.columns;

  // Header row
  const header = ['Participant', ...columns.map(c => c.question)];

  if (columns.length === 0) return [header];

  // Overview row
  const overviewRow = ['Overview', ...columns.map(col => {
    const ov = overviews.get(`${grid.id}_${col.id}`);
    if (!ov || ov.themes.length === 0) return '';
    return ov.themes.map(t => `${t.name} (${t.count}): ${t.summary}`).join('\n');
  })];

  // Participant rows
  const participantRows = participants.map(p => {
    const name = p.name;
    const cellValues = columns.map(col => {
      const cell = cells.get(`${grid.id}_${col.id}_${p.id}`);
      return cell?.summary || 'No data';
    });
    return [name, ...cellValues];
  });

  return [header, overviewRow, ...participantRows];
};
